import React, { useEffect, useState } from 'react';
import { Modal, Spin, Empty } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { MakeApiCall } from '../../../../../apis';

// Format with date and time
const formatDateTime = (ts) =>
    new Date(ts * 1000).toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

// Format just date for x-axis (to avoid crowding)
const formatDateForXAxis = (ts) =>
    new Date(ts * 1000).toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short'
    });

// Format with full date and time for tooltip
const formatTooltipDateTime = (ts) =>
    new Date(ts * 1000).toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

// Fetch all records
const fetchAllPages = async (asin, startDate, endDate, token) => {
    const res = await MakeApiCall(
        'products/sales-rank-history?no_pagination=true',
        'post',
        { asin, start_date: startDate, end_date: endDate },
        true,
        {},
        token
    );
    if (!res?.status) throw new Error(res?.message || 'Failed to load BSR history');
    return res.data?.records || [];
};

// Process records: Keep all records with their timestamps
const processRecords = (records) => {
    if (!records.length) return { 
        timestamps: [], 
        subData: [], 
        rootData: [], 
        subLabel: '', 
        rootLabel: '',
        rawRecords: []
    };
    
    // Sort by created_at (oldest to newest)
    const sorted = [...records].sort((a, b) => a.created_at - b.created_at);
    
    // For chart - show each record as a point (not grouping by day)
    const timestamps = sorted.map(r => r.created_at);
    const subData = sorted.map(r => r.subcategory_sales_rank);
    const rootData = sorted.map(r => r.root_category_sales_rank);
    const xAxisLabels = sorted.map(r => formatDateForXAxis(r.created_at));
    
    // Get category titles
    const subLabel = records[0]?.subcategory_title || 'Subcategory';
    const rootLabel = records[0]?.root_category_title || 'Root Category';
    
    return { 
        timestamps, 
        subData, 
        rootData, 
        xAxisLabels,
        subLabel, 
        rootLabel,
        rawRecords: sorted
    };
};

const SalesRankModal = ({ open, onClose, asin, productTitle, startDate, endDate, token }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartInfo, setChartInfo] = useState(null);

    useEffect(() => {
        if (!open || !asin) return;

        const load = async () => {
            setLoading(true);
            setError(null);
            setChartInfo(null);
            try {
                const records = await fetchAllPages(asin, startDate, endDate, token);
                
                if (!records.length) {
                    setChartInfo({ 
                        timestamps: [], 
                        subData: [], 
                        rootData: [], 
                        xAxisLabels: [],
                        subLabel: '', 
                        rootLabel: '',
                        rawRecords: []
                    });
                    return;
                }
                
                const processed = processRecords(records);
                setChartInfo(processed);
            } catch (e) {
                setError(e.message || 'Failed to load BSR history');
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, asin, startDate, endDate, token]);

    const hasData = chartInfo && chartInfo.timestamps && chartInfo.timestamps.length > 0;

    const subColor = '#314F71';
    const rootColor = '#f45b68';

    const chartOptions = {
        chart: {
            type: 'line',
            toolbar: false,
            zoom: { enabled: false, type: 'x', autoScaleYaxis: true },
            fontFamily: 'inherit',
            background: 'transparent',
            animations: { enabled: true, easing: 'easeinout', speed: 500 },
        },
        colors: [subColor, rootColor],
        stroke: {
            curve: 'smooth',
            width: [2.5, 2.5],
        },
        markers: {
            size: 3,
            strokeColors: [subColor, rootColor],
            strokeWidth: 2,
            fillColor: '#fff',
            hover: { size: 5 },
        },
        xaxis: {
            categories: hasData ? chartInfo.xAxisLabels : [],
            tickAmount: hasData ? Math.min(chartInfo.timestamps.length, 10) : 5,
            labels: {
                rotate: 0,
                style: { fontSize: '12px', colors: '#888' },
                formatter: (value, index) => {
                    if (hasData && chartInfo.timestamps[index]) {
                        return formatDateForXAxis(chartInfo.timestamps[index]);
                    }
                    return value;
                },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
            // title: {
            //     text: 'Date & Time',
            //     style: { fontSize: '12px', fontWeight: 500 },
            // },
        },
        yaxis: [
            {
                reversed: true,
                // title: {
                //     text: hasData ? chartInfo.subLabel : 'Subcategory Rank',
                //     style: { fontSize: '12px', color: subColor, fontWeight: 500 },
                // },
                labels: {
                    style: { fontSize: '11px', colors: subColor },
                    formatter: (val) => {
                        if (val === null || val === undefined) return '—';
                        return `#${Math.round(val).toLocaleString()}`;
                    },
                },
            },
            {
                reversed: true,
                opposite: true,
                // title: {
                //     text: hasData ? chartInfo.rootLabel : 'Root Category Rank',
                //     style: { fontSize: '12px', color: rootColor, fontWeight: 500 },
                // },
                labels: {
                    style: { fontSize: '11px', colors: rootColor },
                    formatter: (val) => {
                        if (val === null || val === undefined) return '—';
                        return `#${Math.round(val).toLocaleString()}`;
                    },
                },
            },
        ],
        tooltip: {
            theme: 'light',
            shared: true,
            intersect: false,
            x: {
                formatter: (value, { dataPointIndex }) => {
                    if (chartInfo && chartInfo.timestamps && chartInfo.timestamps[dataPointIndex]) {
                        return formatTooltipDateTime(chartInfo.timestamps[dataPointIndex]);
                    }
                    return value;
                },
            },
            y: {
                formatter: (val, { series, seriesIndex }) => {
                    if (val === null || val === undefined) return '—';
                    return `#${Math.round(val).toLocaleString()}`;
                },
            },
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'center',
            fontSize: '12px',
            markers: { width: 10, height: 10, radius: 3 },
            itemMargin: { horizontal: 12 },
        },
        grid: {
            borderColor: '#f0f0f0',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        dataLabels: { enabled: false },
    };

    const series = hasData
        ? [
            { name: chartInfo.subLabel, data: chartInfo.subData },
            { name: chartInfo.rootLabel, data: chartInfo.rootData },
          ]
        : [];

    // Calculate statistics
    const latestSub = hasData ? chartInfo.subData[chartInfo.subData.length - 1] : null;
    const latestRoot = hasData ? chartInfo.rootData[chartInfo.rootData.length - 1] : null;
    const bestSub = hasData ? Math.min(...chartInfo.subData.filter(v => v != null)) : null;
    const bestRoot = hasData ? Math.min(...chartInfo.rootData.filter(v => v != null)) : null;
    const worstSub = hasData ? Math.max(...chartInfo.subData.filter(v => v != null)) : null;
    const worstRoot = hasData ? Math.max(...chartInfo.rootData.filter(v => v != null)) : null;
    
    // Get first and last date-time
    const firstDateTime = hasData && chartInfo.timestamps[0] ? formatDateTime(chartInfo.timestamps[0]) : null;
    const lastDateTime = hasData && chartInfo.timestamps[chartInfo.timestamps.length - 1] ? formatDateTime(chartInfo.timestamps[chartInfo.timestamps.length - 1]) : null;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1100}
            title={
                <div className="d-flex flex-column gap-1">
                    <span className="fw-bold fs-4 mb-2 text-gray-900">BSR Trend Analysis</span>
                    {/* {productTitle && (
                        <span
                            className="text-muted fs-8 fw-normal"
                            style={{
                                maxWidth: 900, 
                                display: 'block',
                                whiteSpace: 'nowrap', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {productTitle}
                        </span>
                    )} */}
                    <div className="fs-8 ">
                        <span><b>ASIN:</b> {asin}</span>
                        {startDate && endDate && (
                            <span className="ms-3"><i className='ki-outline ki-filter-search text-danger fs-6'></i> Filter: {startDate} → {endDate}</span>
                        )}
                        {hasData && (
                            <>
                                <span className="ms-3"><i className='ki-outline ki-chart-simple-2 text-danger fs-6'></i> {chartInfo.timestamps.length} data points</span>
                                {/* <span className="ms-3">⏰ {firstDateTime} → {lastDateTime}</span> */}
                            </>
                        )}
                    </div>
                </div>
            }
            destroyOnClose
            centered
        >
            {/* Stats Cards */}
            {hasData && (
                <div className="d-flex gap-3 mb-3 flex-wrap">
                {/* Subscribers Card */}
                <div style={{
                    flex: 1, minWidth: 180, padding: '8px 12px',
                    borderRadius: 8, background: '#EEF3FA',
                    borderLeft: `3px solid ${subColor}`,
                }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fs-9 text-muted mb-1">{chartInfo.subLabel}</div>
                            <div className="fw-bold fs-2" style={{ color: subColor, lineHeight: 1.2 }}>
                                #{latestSub?.toLocaleString()}
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="fs-6">
                                <span className="text-success">🏆 {bestSub?.toLocaleString()}</span>
                                <span className="text-muted mx-1">/</span>
                                <span className="text-danger">📉 {worstSub?.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            
                {/* Roots Card */}
                <div style={{
                    flex: 1, minWidth: 180, padding: '8px 12px',
                    borderRadius: 8, background: '#FEF0EB',
                    borderLeft: `3px solid ${rootColor}`,
                }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fs-9 text-muted mb-1">{chartInfo.rootLabel}</div>
                            <div className="fw-bold fs-2" style={{ color: rootColor, lineHeight: 1.2 }}>
                                #{latestRoot?.toLocaleString()}
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="fs-6">
                                <span className="text-success">🏆 {bestRoot?.toLocaleString()}</span>
                                <span className="text-muted mx-1">/</span>
                                <span className="text-danger">📉 {worstRoot?.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            
                {/* Summary Card */}
                <div style={{
                    flex: 0.7, minWidth: 140, padding: '8px 12px',
                    borderRadius: 8, background: '#F4F6F9',
                    borderLeft: '3px solid #adb5bd',
                }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <div className="fs-9 text-muted mb-1">Data Points</div>
                            <div className="fw-bold fs-2 text-gray-700" style={{ lineHeight: 1.2 }}>
                                {chartInfo.timestamps.length}
                            </div>
                        </div>
                        <div className="text-end fs-6 text-muted">
                            <div>📈 real-time</div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Chart */}
            <div style={{ minHeight: 400 }}>
                {loading && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                        <Spin size="large" tip="Loading BSR history..." />
                    </div>
                )}

                {!loading && error && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                        <Empty description={error} />
                    </div>
                )}

                {!loading && !error && chartInfo && chartInfo.timestamps.length === 0 && (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                        <Empty description="No BSR data available for the selected period" />
                    </div>
                )}

                {!loading && !error && hasData && (
                    <>
                        <ReactApexChart
                            options={chartOptions}
                            series={series}
                            type="line"
                            height={350}
                        />
                        {/* <div className="text-muted fs-9 text-center mt-2">
                            💡 Y-axis is inverted — lower rank number = better sales position
                        </div> */}
                    </>
                )}
            </div>

            {/* Data Table with Date and Time */}
            {hasData && (
                <div className="mt-4">
                    <details>
                        <summary className="text-gray-800 fs-6" style={{ cursor: 'pointer' }}>
                            📋 View detailed data with date and time ({chartInfo.timestamps.length} records)
                        </summary>
                        <div style={{ 
                            maxHeight: '300px', 
                            overflow: 'auto',
                            marginTop: '12px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px'
                        }}>
                            <table className="table table-sm table-hover" style={{ fontSize: '12px' }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Date & Time</th>
                                        <th>Subcategory Rank</th>
                                        <th>Root Category Rank</th>
                                        <th>Subcategory Title</th>
                                        <th>Root Category Title</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chartInfo.rawRecords.map((record, idx) => (
                                        <tr key={record.id}>
                                            <td>{idx + 1}</td>
                                            <td>{formatDateTime(record.created_at)}</td>
                                            <td>
                                                <span className="fw-semibold" style={{ color: subColor }}>
                                                    #{record.subcategory_sales_rank?.toLocaleString()}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-semibold" style={{ color: rootColor }}>
                                                    #{record.root_category_sales_rank?.toLocaleString()}
                                                </span>
                                            </td>
                                            <td>{record.subcategory_title}</td>
                                            <td>{record.root_category_title}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </details>
                </div>
            )}
        </Modal>
    );
};

export default SalesRankModal;